import type { ActionMode, DaysActive, TimerProfileFormValues } from "../../components/types";
import { ConfigurationSliderCard } from "../../general-configuration/components/configuration-cards.component";
import { sanitizeNumericInput } from "../hooks/use-timer-profile-validation.hook";

type ZoneOption = {
  id: number;
  name: string;
};

type TimerProfileFormProps = {
  values: TimerProfileFormValues;
  onChange: (values: TimerProfileFormValues) => void;
  errors: Record<string, string>;
  daysOptions: DaysActive[];
  actionModeOptions: ActionMode[];
  zoneOptions: ZoneOption[];
  isSubmitting: boolean;
  onTouchField?: (fieldName: string) => void;
};

export function TimerProfileForm({
  values,
  onChange,
  errors,
  daysOptions,
  actionModeOptions,
  zoneOptions,
  isSubmitting,
  onTouchField,
}: TimerProfileFormProps) {
  const selectDay = (day: DaysActive) => {
    onChange({ ...values, days: [day] });
    onTouchField?.("days");
  };

  const handleDimLevelChange = (value: number) => {
    const sanitized = sanitizeNumericInput(value, 0, 100);
    onChange({ ...values, dimLevel: sanitized });
    onTouchField?.("dimLevel");
  };

  const handleBrightLevelChange = (value: number) => {
    const sanitized = sanitizeNumericInput(value, 0, 100);
    onChange({ ...values, brightLevel: sanitized });
    onTouchField?.("brightLevel");
  };

  return (
    <div className="profile-form">
      <div className="profile-form__row">
        <label className="profile-form__label" htmlFor="profile-name">
          Profile Name
        </label>
        <input
          id="profile-name"
          className="profile-form__input"
          type="text"
          placeholder="e.g., Evening Ambience"
          value={values.name}
          onChange={(event) => {
            onChange({ ...values, name: event.target.value });
            onTouchField?.("name");
          }}
          disabled={isSubmitting}
        />
        {errors.name && (
          <span className="profile-form__error">{errors.name}</span>
        )}
      </div>

      <div className="profile-form__row">
        <label className="profile-form__label">Time Span</label>
        <div className="profile-form__time-group">
          <input
            className="profile-form__input"
            type="time"
            value={values.startTime}
            onChange={(event) =>
              {
                onChange({ ...values, startTime: event.target.value });
                onTouchField?.("startTime");
              }
            }
            disabled={isSubmitting || values.validAllDay}
          />
          <span className="profile-form__time-separator">—</span>
          <input
            className="profile-form__input"
            type="time"
            value={values.endTime}
            onChange={(event) =>
              {
                onChange({ ...values, endTime: event.target.value });
                onTouchField?.("endTime");
              }
            }
            disabled={isSubmitting || values.validAllDay}
          />
        </div>
        {errors.startTime && (
          <span className="profile-form__error">{errors.startTime}</span>
        )}
        {errors.endTime && (
          <span className="profile-form__error">{errors.endTime}</span>
        )}
        <div className="profile-form__checkbox-label">
          <input
            id="valid-all-day"
            type="checkbox"
            checked={values.validAllDay}
            onChange={(event) => {
              const validAllDay = event.target.checked;
              onChange(
                validAllDay
                  ? { ...values, validAllDay, startTime: "00:00", endTime: "23:59" }
                  : { ...values, validAllDay }
              );
            }}
            disabled={isSubmitting}
          />
          <span>Valid All Day</span>
        </div>
      </div>

      <div className="profile-form__row">
        <label className="profile-form__label">Days Active</label>
        <div className="profile-days">
          {daysOptions.map((day) => (
            <button
              key={day}
              type="button"
              className={`profile-days__button ${
                values.days[0] === day
                  ? "profile-days__button--active"
                  : ""
              }`}
              onClick={() => selectDay(day)}
              disabled={isSubmitting}
            >
              {day}
            </button>
          ))}
        </div>
        {errors.days && (
          <span className="profile-form__error">{errors.days}</span>
        )}
      </div>

      <div className="profile-form__row">
        <ConfigurationSliderCard
          title="Dim Level"
          subtitle="Minimum brightness level"
          suffix="%"
          value={values.dimLevel}
          min={0}
          max={100}
          step={1}
          onValueChange={handleDimLevelChange}
          onClick={() => handleDimLevelChange(0)}
          icon=""
          error={errors.dimLevel}
        />
      </div>

      <div className="profile-form__row">
        <ConfigurationSliderCard
          title="Bright Level"
          subtitle="Maximum brightness level"
          suffix="%"
          value={values.brightLevel}
          min={0}
          max={100}
          step={1}
          onValueChange={handleBrightLevelChange}
          onClick={() => handleBrightLevelChange(0)}
          icon=""
          error={errors.brightLevel}
        />
      </div>

      <div className="profile-form__row">
        <label className="profile-form__label">Light Action Mode</label>
        <div className="profile-action-mode">
          {actionModeOptions.map((mode) => (
            <button
              key={mode}
              type="button"
              className={`profile-action-mode__button ${
                values.actionMode === mode
                  ? "profile-action-mode__button--active"
                  : ""
              }`}
              onClick={() => {
                onChange({ ...values, actionMode: mode });
                onTouchField?.("actionMode");
              }}
              disabled={isSubmitting}
            >
              {mode}
            </button>
          ))}
        </div>
        {errors.actionMode && (
          <span className="profile-form__error">{errors.actionMode}</span>
        )}
      </div>

      <div className="profile-form__row">
        <label className="profile-form__label" htmlFor="profile-zone">
          Zone
        </label>
        <div className="profile-form__select-wrap">
          <div className="profile-form__select-control">
            <select
              id="profile-zone"
              className="profile-form__input profile-form__select"
              value={values.zoneId ?? ""}
              onChange={(event) =>
                {
                  onChange({
                    ...values,
                    zoneId: event.target.value ? Number(event.target.value) : null,
                  });
                  onTouchField?.("zoneId");
                }
              }
              disabled={isSubmitting}
            >
              <option value="">Select Zone</option>
              {zoneOptions.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </div>
          {errors.zoneId && (
            <span className="profile-form__error">{errors.zoneId}</span>
          )}
        </div>
      </div>
    </div>
  );
}
